// ======================================================================
// FILE: src/modules/wms/sla/components/SLARulesTab.js
// MODULE: Work Management System (WMS)
// FEATURE: SLA Management
//
// PURPOSE:
//   SLA Rule Management Component
//   Add / Edit / Delete SLA Rules
//
// USED BY:
//   SLAPolicyFormPage.js
//
// DEPENDENCIES:
//   wmsSLAService
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Reusable Component
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsSLAService
from "../../services/wmsSLAService";

const DEFAULT_FORM = {

  priority: "",

  target_duration: "",

  duration_unit: "HOURS",

  warning_percentage: 80,

  breach_percentage: 100,

  auto_escalation_enabled: true,

  description: "",
  remarks: "",
};

export default function SLARulesTab({

  policyId,
  isEdit,
}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showForm, setShowForm] =
    useState(false);

  const [editingRuleId, setEditingRuleId] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  useEffect(() => {

    if (!isEdit) return;

    loadRules();

  }, [policyId]);

  async function loadRules() {

    try {

      setLoading(true);

      const data =
        await wmsSLAService.getRules(
          policyId
        );

      setRows(data || []);

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load SLA rules"
      );

    } finally {

      setLoading(false);
    }
  }

  function resetForm() {

    setEditingRuleId(null);

    setForm(DEFAULT_FORM);

    setShowForm(false);
  }

  function handleChange(
    field,
    value
  ) {

    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }

  function openCreate() {

    setEditingRuleId(null);

    setForm(DEFAULT_FORM);

    setShowForm(true);
  }

  function openEdit(
    row
  ) {

    setEditingRuleId(row.id);

    setForm({

      priority:
        row.priority || "",

      target_duration:
        row.target_duration || "",

      duration_unit:
        row.duration_unit || "HOURS",

      warning_percentage:
        row.warning_percentage || 80,

      breach_percentage:
        row.breach_percentage || 100,

      auto_escalation_enabled:
        row.auto_escalation_enabled,

      description:
        row.description || "",

      remarks:
        row.remarks || "",
    });

    setShowForm(true);
  }

  function validate() {

    if (!form.priority) {

      alert(
        "Priority is required"
      );

      return false;
    }

    if (
      !form.target_duration
    ) {

      alert(
        "Target Duration is required"
      );

      return false;
    }

    return true;
  }

  async function saveRule() {

    if (!validate()) {
      return;
    }

    try {

      const payload = {

        priority:
          form.priority,

        target_duration:
          Number(
            form.target_duration
          ),

        duration_unit:
          form.duration_unit,

        warning_percentage:
          Number(
            form.warning_percentage
          ),

        breach_percentage:
          Number(
            form.breach_percentage
          ),

        auto_escalation_enabled:
          form.auto_escalation_enabled,

        description:
          form.description,

        remarks:
          form.remarks,
      };

      if (
        editingRuleId
      ) {

        await wmsSLAService.updateRule(
          policyId,
          editingRuleId,
          payload
        );

      } else {

        await wmsSLAService.createRule(
          policyId,
          payload
        );
      }

      resetForm();

      loadRules();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  async function deleteRule(
    row
  ) {

    const ok =
      window.confirm(
        `Delete rule ${row.priority}?`
      );

    if (!ok) return;

    try {

      await wmsSLAService.deleteRule(
        policyId,
        row.id
      );

      loadRules();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  if (!isEdit) {

    return (

      <div className="card">

        <div className="card-body">

          <div className="alert alert-info mb-0">

            Save SLA Policy first before
            adding SLA Rules.

          </div>

        </div>

      </div>
    );
  }

  return (

    <div className="card mt-3">

      <div className="card-header d-flex justify-content-between align-items-center">

        <h5 className="mb-0">
          SLA Rules
        </h5>

        <button
          className="btn btn-primary"
          onClick={openCreate}
        >
          + Add Rule
        </button>

      </div>

      <div className="card-body">

        {showForm && (

          <div className="card mb-3 border">

            <div className="card-body">

              <div className="row">

                <div className="col-md-2 mb-3">

                  <label>
                    Priority *
                  </label>

                  <select
                    className="form-control"
                    value={form.priority}
                    onChange={(e) =>
                      handleChange(
                        "priority",
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    <option value="LOW">
                      LOW
                    </option>

                    <option value="MEDIUM">
                      MEDIUM
                    </option>

                    <option value="HIGH">
                      HIGH
                    </option>

                    <option value="CRITICAL">
                      CRITICAL
                    </option>

                  </select>

                </div>

                <div className="col-md-2 mb-3">

                  <label>
                    Duration *
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.target_duration
                    }
                    onChange={(e) =>
                      handleChange(
                        "target_duration",
                        e.target.value
                      )
                    }
                  />

                </div>

                <div className="col-md-2 mb-3">

                  <label>
                    Unit
                  </label>

                  <select
                    className="form-control"
                    value={
                      form.duration_unit
                    }
                    onChange={(e) =>
                      handleChange(
                        "duration_unit",
                        e.target.value
                      )
                    }
                  >
                    <option value="MINUTES">
                      Minutes
                    </option>

                    <option value="HOURS">
                      Hours
                    </option>

                    <option value="DAYS">
                      Days
                    </option>

                  </select>

                </div>

                <div className="col-md-2 mb-3">

                  <label>
                    Warning %
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.warning_percentage
                    }
                    onChange={(e) =>
                      handleChange(
                        "warning_percentage",
                        e.target.value
                      )
                    }
                  />

                </div>

                <div className="col-md-2 mb-3">

                  <label>
                    Breach %
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.breach_percentage
                    }
                    onChange={(e) =>
                      handleChange(
                        "breach_percentage",
                        e.target.value
                      )
                    }
                  />

                </div>

                <div className="col-md-2 mb-3">

                  <label>
                    Escalation
                  </label>

                  <div>

                    <input
                      type="checkbox"
                      checked={
                        form.auto_escalation_enabled
                      }
                      onChange={(e) =>
                        handleChange(
                          "auto_escalation_enabled",
                          e.target.checked
                        )
                      }
                    />

                  </div>

                </div>

              </div>

              <button
                className="btn btn-success me-2"
                onClick={saveRule}
              >
                Save Rule
              </button>

              <button
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>

            </div>

          </div>
        )}

        <table className="table table-bordered table-hover">

          <thead>

            <tr>

              <th>Priority</th>

              <th>Duration</th>

              <th>Unit</th>

              <th>Warning %</th>

              <th>Breach %</th>

              <th>Auto Esc.</th>

              <th width="160">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading && (

              <tr>

                <td
                  colSpan="7"
                  className="text-center"
                >
                  Loading...
                </td>

              </tr>
            )}

            {!loading &&
              rows.length === 0 && (

              <tr>

                <td
                  colSpan="7"
                  className="text-center"
                >
                  No Rules Found
                </td>

              </tr>
            )}

            {!loading &&
              rows.map((row) => (

              <tr key={row.id}>

                <td>
                  {row.priority}
                </td>

                <td>
                  {row.target_duration}
                </td>

                <td>
                  {row.duration_unit}
                </td>

                <td>
                  {row.warning_percentage}
                </td>

                <td>
                  {row.breach_percentage}
                </td>

                <td>
                  {row.auto_escalation_enabled
                    ? "Yes"
                    : "No"}
                </td>

                <td>

                  <button
                    className="btn btn-sm btn-warning me-1"
                    onClick={() =>
                      openEdit(row)
                    }
                  >
                    Edit
                  </button>

                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() =>
                      deleteRule(row)
                    }
                  >
                    Delete
                  </button>

                </td>

              </tr>
            ))}

          </tbody>

        </table>

      </div>

    </div>
  );
}